import { Component, EventEmitter, OnDestroy, OnInit, OnChanges, SimpleChanges, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { AppService } from '../../../store/app.service';
import { TranslateService } from '@ngx-translate/core';
import { ReportsApiService } from '../reports.apiservice';
import { CampaignsApiService } from '../../campaigns/campaigns.apiservice';
import { environment } from '../../../../environments/environment';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { ILead } from '../../../core/models/lead.models';
import { LeadsApiService } from '../../leads/leads.apiservice';
import { takeUntil } from 'rxjs/operators';
import { get } from 'underscore';
import { InstanceService } from '../../../core/instance/instance.service';
import { UtilityService } from '../../../core/helpers/utility.service';
import { ROLES } from '../../../core/helpers/constants';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { CampaignAnalysisModalComponent } from '../../../shared/modals/campaign-analysis/campaign-analysis-modal.component';

@Component({
  selector: 'app-report-view',
  templateUrl: './report-view.component.html',
  styleUrls: ['./report-view.component.scss']
})
export class ReportViewComponent implements OnInit, OnDestroy, OnChanges {
  readonly conversionActionOptions: Array<{
    key: string;
    labelKey: string;
    singularLabelKey: string;
    aliases: string[];
    metaActionTypes: string[];
    metaActionAggregation?: 'sum' | 'max';
  }> = [
    { key: 'leads', labelKey: 'LEADS', singularLabelKey: 'LEAD', aliases: ['leads'], metaActionTypes: ['lead', 'leadgen.other', 'onsite_conversion.lead_grouped'] },
    { key: 'purchases', labelKey: 'PURCHASES', singularLabelKey: 'PURCHASE', aliases: ['purchases'], metaActionTypes: ['purchase', 'omni_purchase', 'offsite_conversion.fb_pixel_purchase'], metaActionAggregation: 'max' },
    { key: 'add_to_cart', labelKey: 'ADD_TO_CART', singularLabelKey: 'ADD_TO_CART', aliases: ['add_to_cart'], metaActionTypes: ['add_to_cart', 'omni_add_to_cart', 'offsite_conversion.fb_pixel_add_to_cart'], metaActionAggregation: 'max' },
    { key: 'checkout', labelKey: 'CHECKOUT', singularLabelKey: 'CHECKOUT', aliases: ['checkout', 'initiate_checkout'], metaActionTypes: ['initiate_checkout', 'omni_initiated_checkout', 'offsite_conversion.fb_pixel_initiate_checkout'], metaActionAggregation: 'max' },
    { key: 'booking', labelKey: 'BOOKING', singularLabelKey: 'BOOKING', aliases: ['booking', 'schedule'], metaActionTypes: ['schedule'] },
    { key: 'calls', labelKey: 'CALLS', singularLabelKey: 'CALL', aliases: ['calls'], metaActionTypes: [] },
    { key: 'book', labelKey: 'BOOK', singularLabelKey: 'BOOK', aliases: ['book'], metaActionTypes: [] },
    { key: 'show', labelKey: 'SHOW', singularLabelKey: 'SHOW', aliases: ['show'], metaActionTypes: [] },
    { key: 'enroll', labelKey: 'ENROLL', singularLabelKey: 'ENROLL', aliases: ['enroll'], metaActionTypes: [] },
    { key: 'revenue', labelKey: 'REVENUE', singularLabelKey: 'REVENUE', aliases: ['revenue'], metaActionTypes: [] },
    { key: 'trial_started', labelKey: 'TRIAL_STARTED', singularLabelKey: 'TRIAL_STARTED', aliases: ['trial_started', 'start_trial'], metaActionTypes: ['start_trial', 'omni_start_trial', 'offsite_conversion.fb_pixel_start_trial', 'start_trial_total', 'start_trial_website'], metaActionAggregation: 'max' },
    { key: 'subscription', labelKey: 'SUBSCRIPTION', singularLabelKey: 'SUBSCRIPTION', aliases: ['subscription', 'subscribe'], metaActionTypes: ['subscribe', 'omni_subscribe', 'offsite_conversion.fb_pixel_subscribe', 'subscribe_total', 'subscribe_website'], metaActionAggregation: 'max' },
  ];

  readonly salesFunnelActionDefinitions: Array<{
    stage: 'Booked' | 'Show' | 'Sold';
    key: string;
    labelKey: string;
    platform?: string;
  }> = [
    { stage: 'Booked', key: 'book', labelKey: 'BOOK' },
    { stage: 'Booked', key: 'booking', labelKey: 'BOOKING', platform: 'Google' },
    { stage: 'Booked', key: 'schedule', labelKey: 'SCHEDULE', platform: 'Meta' },
    { stage: 'Booked', key: 'add_to_cart', labelKey: 'ADD_TO_CART' },
    { stage: 'Show', key: 'show', labelKey: 'SHOW' },
    { stage: 'Show', key: 'trial_started', labelKey: 'TRIAL_STARTED' },
    { stage: 'Show', key: 'initiate_checkout', labelKey: 'CHECKOUT' },
    { stage: 'Sold', key: 'purchases', labelKey: 'PURCHASES' },
    { stage: 'Sold', key: 'revenue', labelKey: 'REVENUE' },
  ];

  currencyPipeFormat: string = '1.2-2';
  @Input() Iid: string | null = null;
  @Input() iReportData: any | null = null;
  @Input() iReportName: string | null = null;
  @Input() iIsRollupReport = false;
  @Input() iShowControls = true;
  @Input() iSelectedConversionActions: string[] | null = null;
  @Output() reportDateRangeChange = new EventEmitter<{ dateStart: string | null; dateStop: string | null }>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  reportMetrics: any = { AllCampaigns: [] };
  previousReportMetrics: any = {};
  private adPreviewRequests = new Map<string, Promise<string>>();
  dailyLeads: { Date: string; Conversions: number }[] = [];
  dailyLeadsChartType: ChartType = 'line';
  dailyLeadsChartLabels: string[] = [];
  dailyLeadsChartDatasets: ChartDataSets[] = [];
  dailyLeadsChartColors = [{
    borderColor: '#1877F2',
    backgroundColor: 'rgba(24, 119, 242, 0.12)',
    pointBackgroundColor: '#1877F2',
    pointBorderColor: '#1877F2',
    pointHoverBackgroundColor: '#ffffff',
    pointHoverBorderColor: '#1877F2'
  }];
  dailyLeadsChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 250
    },
    legend: {
      display: false
    },
    elements: {
      line: {
        tension: 0.3,
        borderWidth: 3,
        fill: false
      },
      point: {
        radius: 3,
        hitRadius: 10,
        hoverRadius: 5
      }
    },
    scales: {
      xAxes: [{
        gridLines: {
          display: false
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
          fontColor: '#6b7280'
        }
      }],
      yAxes: [{
        gridLines: {
          color: 'rgba(107, 114, 128, 0.16)'
        },
        ticks: {
          precision: 0,
          fontColor: '#6b7280'
        }
      }]
    },
    tooltips: {
      mode: 'index',
      intersect: false
    },
    hover: {
      mode: 'index',
      intersect: false
    }
  };
  conversionActionBreakdown: Array<{ label: string; value: number; key: string; labelKey: string }> = [];
  selectedConversionActions: string[] = [];
  salesFunnelActions: Array<{ stage: string; key: string; labelKey: string; value: number }> = [];
  selectedSalesFunnelActions: string[] = [];
  private currentReportData: any = null;
  private previousReportData: any = null;
  private usesLeadCampaignConversionDefaults = false;
  platform: string = '';
  campaignName: string = '';
  reportForm: FormGroup;
  reportPeriods: Array<IReportingPeriod & { displayKey: string; title: string }> = [];
  DateStart: string | null = null;
  DateStop: string | null = null;
  selectedPeriod: string | null = null;
  loadCompleted = false;
  hasRunReport = false;
  validPlatform = true;
  isInternalUser = false;
  isAdmin = false;

  private emitReportDateRange(): void {
    this.reportDateRangeChange.emit({
      dateStart: this.DateStart,
      dateStop: this.DateStop
    });
  }

  leadsTotalCount: number = 0;
  leadsData: Array<ILead> = new Array<ILead>();
  membersTotalCount: number = 0;
  membersData: Array<ILead> = new Array<ILead>();

  defaultGoogleColumns: any[] = [];
  defaultMetaColumns: any[] = [];

  getCurrencyPipeArgs(): [string, string, string] {
    const currency = (this.reportMetrics.Currency || 'USD').toUpperCase();
    switch (currency) {
      case 'JPY':
        this.currencyPipeFormat = '1.0-0';
        return ['JPY', 'symbol', this.currencyPipeFormat];
      case 'EUR':
        this.currencyPipeFormat = '1.2-2';
        return ['EUR', 'symbol', this.currencyPipeFormat];
      case 'MXN':
      case 'PESO':
      case 'PESOS':
        this.currencyPipeFormat = '1.2-2';
        return ['MXN', 'symbol', this.currencyPipeFormat];
      default:
        this.currencyPipeFormat = '1.2-2';
        return ['USD', 'symbol', this.currencyPipeFormat];
    }
  }

  get hasMultipleConversionActions(): boolean {
    return this.conversionActionBreakdown.length > 1;
  }

  get hasConversionActionBreakdown(): boolean {
    return this.conversionActionBreakdown.length > 0;
  }

  get isLeadOnlyReport(): boolean {
    if (this.selectedConversionActions.length > 0) {
      return this.selectedConversionActions.length === 1 && this.selectedConversionActions[0] === 'leads';
    }

    return this.conversionActionBreakdown.length === 0 ||
      (this.conversionActionBreakdown.length === 1 && this.conversionActionBreakdown[0].key === 'leads');
  }

  get hasSalesFunnelActionData(): boolean {
    const leadActionCount = this.getSalesFunnelLeadDataCount() > 0 ? 1 : 0;
    return leadActionCount + this.salesFunnelActions.length >= 2;
  }

  get canShowSalesFunnel(): boolean {
    return this.hasSalesFunnelActionData || this.conversionActionBreakdown.length === 1;
  }

  get showSalesFunnel(): boolean {
    return this.canShowSalesFunnel && !!this.reportForm?.get('showCaculator')?.value;
  }

  get conversionActionCountLabel(): string {
    if (this.isLeadOnlyReport) {
      return this.translate.instant('LEADS');
    }

    if (this.conversionActionBreakdown.length === 1) {
      return this.translate.instant(this.conversionActionBreakdown[0].labelKey ?? 'CONVERSION_ACTIONS');
    }

    return this.translate.instant(this.hasMultipleConversionActions ? 'CONVERSION_ACTIONS' : 'LEADS');
  }

  get conversionActionRateLabel(): string {
    return this.translate.instant(this.isLeadOnlyReport ? 'LEAD_SUBMISSION_RATE_LSR' : 'CONVERSION_PERCENT_KPI');
  }

  get conversionActionCostLabel(): string {
    return this.translate.instant(this.isLeadOnlyReport ? 'COST_PER_LEAD_CPL' : 'COST_PER_ACTION_CPA');
  }

  updateCurrencyColumns() {
    const currencyArgs = this.getCurrencyPipeArgs();
    const conversionTitle = this.isLeadOnlyReport
      ? 'LEADS'
      : this.conversionActionBreakdown.length === 1
      ? this.conversionActionBreakdown[0].labelKey ?? 'LEADS'
      : this.hasMultipleConversionActions ? 'CONV' : 'LEADS';
    const rateTitle = this.isLeadOnlyReport
      ? 'LSR'
      : this.hasMultipleConversionActions
      ? 'CONV_PERCENT'
      : this.conversionActionBreakdown.length === 1
        ? this.translate.instant('CONVERSION_TYPE_PERCENT', {
            action: this.translate.instant(
              this.conversionActionOptions.find(option => option.key === this.conversionActionBreakdown[0].key)?.singularLabelKey ?? 'CONVERSION_ACTIONS'
            )
          })
        : 'LSR';
    const costTitle = this.isLeadOnlyReport
      ? 'CPL'
      : this.hasMultipleConversionActions
      ? 'CPA'
      : this.conversionActionBreakdown.length === 1
        ? this.translate.instant('COST_PER_CONVERSION_TYPE', {
            action: this.translate.instant(
              this.conversionActionOptions.find(option => option.key === this.conversionActionBreakdown[0].key)?.singularLabelKey ?? 'CONVERSION_ACTIONS'
            )
          })
        : 'CPL';

    this.defaultGoogleColumns = [
      { title: 'COST', field: 'Cost', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
      { title: 'IMPR', field: 'Impressions', sortable: true, pipe: 'NumberPipe' },
      { title: 'CLICKS', field: 'Clicks', sortable: true },
      { title: 'CTR', field: 'Ctr', sortable: true, pipe: 'PercentPipe' },
      { title: 'CPC', field: 'Cpc', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
      { title: conversionTitle, field: 'Conversions', sortable: true },
      { title: rateTitle, field: 'Lsr', sortable: true, pipe: 'PercentPipe' },
      { title: costTitle, field: 'Cpl', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
    ];
    this.defaultMetaColumns = [
      { title: 'COST', field: 'Cost', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
      { title: 'IMPR', field: 'Impressions', sortable: true, pipe: 'NumberPipe' },
      { title: 'CPM', field: 'Cpm', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
      { title: 'CLICKS', field: 'Clicks', sortable: true },
      { title: 'CTR', field: 'Ctr', sortable: true, pipe: 'PercentPipe' },
      { title: conversionTitle, field: 'Conversions', sortable: true },
      { title: rateTitle, field: 'Lsr', sortable: true, pipe: 'PercentPipe' },
      { title: costTitle, field: 'Cpl', sortable: true, pipe: 'Currency', pipeArgs: currencyArgs },
    ];
  }

  private toConversionActionLabelKey(value: string): string {
    switch ((value || '').toLowerCase().trim()) {
      case 'booking':
      case 'schedule':
        return 'BOOKING';
      case 'add_to_cart':
        return 'ADD_TO_CART';
      case 'checkout':
      case 'initiate_checkout':
        return 'CHECKOUT';
      case 'calls':
        return 'CALLS';
      case 'leads':
      case 'lead':
        return 'LEADS';
      case 'purchases':
        return 'PURCHASES';
      default:
        return value.toString().trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toUpperCase();
    }
  }

  private normalizeActionKey(value: string): string {
    return (value ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private toNumber(value: any): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private getRawActionMaps(source: any): any[] {
    if (!source || typeof source !== 'object') {
      return [];
    }

    const mapNames = new Set([
      'ActionCounts',
      'ConversionActions',
      'ActionMap',
      'ActionsBreakdown',
      'ActionBreakdown',
      'ActionTypes'
    ].map(name => this.normalizeActionKey(name)));

    return Object.entries(source)
      .filter(([name, map]) => mapNames.has(this.normalizeActionKey(name)) && map && typeof map === 'object')
      .map(([, map]) => map);
  }

  private getActionValue(source: any, actionKey: string): number {
    const option = this.conversionActionOptions.find(item => item.key === actionKey);
    if (!option) {
      return 0;
    }

    const possibleValues: number[] = [];
    const acceptedKeys = new Set(option.aliases.map(key => this.normalizeActionKey(key)));
    const actionMaps = this.getRawActionMaps(source);
    for (const map of actionMaps.length > 0 ? actionMaps : [source]) {
      const entries = Array.isArray(map)
        ? map.map(item => [item?.key ?? item?.label ?? item?.name, item?.value ?? item?.count])
        : Object.entries(map);

      for (const [rawKey, rawValue] of entries) {
        if (!acceptedKeys.has(this.normalizeActionKey(rawKey))) {
          continue;
        }

        const value = Number((rawValue as any)?.value ?? (rawValue as any)?.count ?? rawValue);
        if (Number.isFinite(value)) {
          possibleValues.push(value);
        }
      }
    }

    return possibleValues.length > 0 ? Math.max(...possibleValues) : 0;
  }

  private getExactActionValue(source: any, actionKey: string): number {
    if (!source || typeof source !== 'object') {
      return 0;
    }

    const normalizedKey = this.normalizeActionKey(actionKey);
    const actionMaps = this.getRawActionMaps(source);
    const values: number[] = [];
    for (const map of actionMaps.length > 0 ? actionMaps : [source]) {
      const entries = Array.isArray(map)
        ? map.map(item => [item?.key ?? item?.label ?? item?.name, item?.value ?? item?.count])
        : Object.entries(map);

      for (const [rawKey, rawValue] of entries) {
        if (this.normalizeActionKey(rawKey) !== normalizedKey) {
          continue;
        }

        const value = Number((rawValue as any)?.value ?? (rawValue as any)?.count ?? rawValue);
        if (Number.isFinite(value)) {
          values.push(value);
        }
      }
    }

    if (values.length > 0) {
      return Math.max(...values);
    }

    return actionKey === 'leads' ? this.toNumber(source.Leads ?? source.leads) : 0;
  }

  private getCanonicalActionCounts(source: any): Record<string, number> {
    return this.conversionActionOptions.reduce((counts, option) => {
      counts[option.key] = this.getActionValue(source, option.key);
      return counts;
    }, {} as Record<string, number>);
  }

  private getMetaActionCounts(actions: any[], source?: any): Record<string, number> {
    const counts = this.conversionActionOptions.reduce((result, option) => {
      const values = (actions ?? [])
        .filter(action => option.metaActionTypes.includes((action?.ActionType ?? action?.action_type ?? '').toString().toLowerCase()))
        .map(action => this.toNumber(action?.Value ?? action?.value));
      result[option.key] = option.metaActionAggregation === 'max'
        ? Math.max(0, ...values)
        : values.reduce((sum, value) => sum + value, 0);
      return result;
    }, {} as Record<string, number>);

    if (source) {
      for (const option of this.conversionActionOptions) {
        counts[option.key] = Math.max(counts[option.key], this.getActionValue(source, option.key));
      }
    }
    return counts;
  }

  private hasActionMap(source: any): boolean {
    return this.getRawActionMaps(source).some(map => Array.isArray(map)
      ? map.length > 0
      : Object.keys(map).some(key => this.conversionActionOptions.some(option =>
          option.aliases.some(alias => this.normalizeActionKey(alias) === this.normalizeActionKey(key)))));
  }

  private initializeConversionActions(data: any): void {
    const counts = this.getCanonicalActionCounts(data);
    const availableActions = this.conversionActionOptions
      .filter(option => counts[option.key] > 0)
      .map(option => ({
        key: option.key,
        label: option.key,
        labelKey: option.labelKey,
        value: counts[option.key]
      }));
    this.usesLeadCampaignConversionDefaults = availableActions.length > 1 &&
      this.hasLeadCampaignNamingToken(this.getPlatformCampaignName(data));
    if (this.usesLeadCampaignConversionDefaults && !availableActions.some(action => action.key === 'leads')) {
      const leadsOption = this.conversionActionOptions.find(option => option.key === 'leads');
      if (leadsOption) {
        availableActions.unshift({
          key: leadsOption.key,
          label: leadsOption.key,
          labelKey: leadsOption.labelKey,
          value: counts[leadsOption.key]
        });
      }
    }

    this.conversionActionBreakdown = availableActions;
    this.selectedConversionActions = this.iSelectedConversionActions
      ? this.conversionActionBreakdown
        .filter(action => this.iSelectedConversionActions!.includes(action.key))
        .map(action => action.key)
      : this.usesLeadCampaignConversionDefaults
        ? ['leads']
        : this.conversionActionBreakdown.map(action => action.key);
    this.initializeSalesFunnelActions(data);
  }

  private applySelectedConversionActions(): void {
    this.selectedConversionActions = this.conversionActionBreakdown
      .filter(action => this.iSelectedConversionActions?.includes(action.key))
      .map(action => action.key);
  }

  private getPlatformCampaignName(data: any): string {
    const platformCampaignName = (data?.PlatformCampaignName ?? '').toString().trim();
    if (platformCampaignName) {
      return platformCampaignName;
    }

    const platform = this.normalizePlatform(data?.Platform || this.platform).toLowerCase();
    return platform === 'google' ? (data?.CampaignName ?? '').toString().trim() : '';
  }

  private hasLeadCampaignNamingToken(platformCampaignName: string): boolean {
    const leadCampaignTokens = new Set([
      'lead',
      'leads',
      'landingpage',
      'lptraffic',
      'websitetraffic',
      'micrositeleads',
      'traffic'
    ]);

    return (platformCampaignName || '').split('_').some(part =>
      leadCampaignTokens.has(part.split('-', 1)[0].trim().toLowerCase())
    );
  }

  private initializeSalesFunnelActions(data: any): void {
    const platform = this.normalizePlatform(data?.Platform || this.platform);
    this.salesFunnelActions = this.salesFunnelActionDefinitions
      .filter(action => !action.platform || action.platform === platform)
      .map(action => ({
        stage: action.stage,
        key: action.key,
        labelKey: action.labelKey,
        value: this.getExactActionValue(data, action.key)
      }))
      .filter(action => action.value > 0);
    this.selectedSalesFunnelActions = this.salesFunnelActions.map(action => action.key);
    this.reportForm.patchValue({
      showCaculator: this.hasSalesFunnelActionData && this.conversionActionBreakdown.length !== 1,
      FunnelLeads: this.getSalesFunnelLeadDataCount(data)
    }, { emitEvent: false });
    this.setSalesFunnelStageInputs(false);
  }

  getSalesFunnelLeadCount(): number {
    const funnelLeadControl = this.reportForm?.get('FunnelLeads');
    return funnelLeadControl
      ? this.toNumber(funnelLeadControl.value)
      : this.getSalesFunnelLeadDataCount();
  }

  private getSalesFunnelLeadDataCount(source: any = this.currentReportData): number {
    if (source && this.hasActionMap(source)) {
      return this.getExactActionValue(source, 'leads');
    }

    return this.toNumber(source?.Leads ?? this.reportMetrics.Leads);
  }

  getSalesFunnelActions(stage: string): Array<{ stage: string; key: string; labelKey: string; value: number }> {
    return this.salesFunnelActions.filter(action => action.stage === stage);
  }

  getSalesFunnelStageTotal(stage: string): number {
    return this.getSalesFunnelActions(stage)
      .filter(action => this.selectedSalesFunnelActions.includes(action.key))
      .reduce((sum, action) => sum + action.value, 0);
  }

  isSalesFunnelActionSelected(actionKey: string): boolean {
    return this.selectedSalesFunnelActions.includes(actionKey);
  }

  toggleSalesFunnelAction(actionKey: string): void {
    if (!this.salesFunnelActions.some(action => action.key === actionKey)) {
      return;
    }

    const selectedIndex = this.selectedSalesFunnelActions.indexOf(actionKey);
    if (selectedIndex === -1) {
      this.selectedSalesFunnelActions.push(actionKey);
    } else {
      this.selectedSalesFunnelActions.splice(selectedIndex, 1);
    }
    this.setSalesFunnelStageInputs();
  }

  private setSalesFunnelStageInputs(updateMetrics = true): void {
    this.reportForm.patchValue({
      Booked: this.getSalesFunnelStageTotal('Booked'),
      Show: this.getSalesFunnelStageTotal('Show'),
      Sold: this.getSalesFunnelStageTotal('Sold')
    }, { emitEvent: false });
    if (updateMetrics) {
      this.updateReportMetrics();
    }
  }

  private setRowConversionActionCounts(row: any, source: any): void {
    const counts = this.getCanonicalActionCounts(source);
    row.ConversionActions = counts;
    row.ActionCounts = counts;
    for (const option of this.conversionActionOptions) {
      row[`action_${option.key}`] = counts[option.key];
    }
  }

  private addConversionActionCounts(target: any, source: any): void {
    const counts = target.ActionCounts ?? target.ConversionActions ?? {};
    for (const option of this.conversionActionOptions) {
      counts[option.key] = this.toNumber(counts[option.key]) + this.getActionValue(source, option.key);
      target[`action_${option.key}`] = counts[option.key];
    }
    target.ActionCounts = counts;
    target.ConversionActions = counts;
  }

  private getSelectedConversionCount(source: any): number {
    if (!this.hasConversionActionBreakdown) {
      return this.toNumber(source?.Conversions ?? source?.conversions ?? source?.Leads ?? source?.leads);
    }

    if (!this.hasActionMap(source) && this.conversionActionBreakdown.length === 1) {
      return this.toNumber(source?.Conversions ?? source?.conversions ?? source?.Leads ?? source?.leads);
    }

    return this.selectedConversionActions.reduce(
      (sum, actionKey) => sum + this.getActionValue(source, actionKey),
      0
    );
  }

  private getSelectedConversionsFromRows(rows: any[]): number {
    return (rows ?? []).reduce((sum, row) => sum + this.getSelectedConversionCount(row), 0);
  }

  private applySelectedConversionsToRows(rows: any[]): void {
    for (const row of rows ?? []) {
      const conversions = this.getSelectedConversionCount(row);
      const clicks = this.toNumber(row.Clicks);
      const cost = this.toNumber(row.Cost);
      row.Conversions = conversions;
      row.Leads = conversions;
      row.SelectedConversionActionsTotal = conversions;
      row.Lsr = clicks > 0 ? conversions / clicks : 0;
      row.Cpl = conversions > 0 ? cost / conversions : 0;
    }
  }

  private getSelectedConversionsFromData(data: any): number {
    if (this.hasActionMap(data)) {
      return this.getSelectedConversionCount(data);
    }

    const rows = data?.DaySplitsAdsPdfModels;
    if (Array.isArray(rows) && rows.length > 0) {
      return this.getSelectedConversionsFromRows(rows);
    }

    return this.toNumber(data?.Conversions ?? data?.Leads);
  }

  allCampaignsColumns: any[] = [];
  devicesColumns: any[] = [];
  adsColumns: any[] = [];
  googleKeywordsColumns: any[] = [];
  metaDemographicsColumns: any[] = [];
  metaAgeDemographicsColumns: any[] = [];
  metaGenderDemographicsColumns: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private reportsApiService: ReportsApiService,
    private campaignsApiService: CampaignsApiService,
    private fb: FormBuilder,
    private appService: AppService,
    private translate: TranslateService,
    private leadsApi: LeadsApiService,
    public instance: InstanceService,
    private modalService: NgbModal,
    private utilityService: UtilityService
  ) {
    const userRoles = this.utilityService.getUserRoles();
    this.isInternalUser = userRoles.some(role => Object.values(ROLES).some(definition => definition.Code === role && definition.IsInternal));
    this.isAdmin = userRoles.includes(ROLES.Admin.Code) || userRoles.includes(ROLES.TechServices.Code);

    this.reportForm = this.fb.group({
      key: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      stopDate: [null, [Validators.required]],
      showLeadsByDay: [true],
      showDevices: [false],
      showKeywords: [false],
      showAds: [false],
      showCaculator: [false],
      showDemographics: [false],
      Booked: [null],
      FunnelLeads: [null],
      Show: [null],
      Sold: [null]
    });

    this.reportForm.get('FunnelLeads')?.valueChanges.subscribe(() => this.updateReportMetrics());
    this.reportForm.get('Booked')?.valueChanges.subscribe(() => this.updateReportMetrics());
    this.reportForm.get('Show')?.valueChanges.subscribe(() => this.updateReportMetrics());
    this.reportForm.get('Sold')?.valueChanges.subscribe(() => this.updateReportMetrics());
  }

  ngOnInit(): void {
    if (this.iReportData) {
      this.platform = this.normalizePlatform(this.iReportData.Platform || '');
      this.campaignName = this.iReportName || this.iReportData.CampaignName || '';
      this.DateStart = this.normalizeReportDate(this.iReportData.StartDate);
      this.DateStop = this.normalizeReportDate(this.iReportData.StopDate);
      this.emitReportDateRange();
      this.reportForm.patchValue({ showDevices: true });
      this.getReportData();
    } else {
      this.loadReport();
    }

    // Ensure ng-select labels update when language changes.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.refreshReportPeriodTitles();
      });

    // Also refresh after translation files are loaded/updated for the current language.
    this.translate.onTranslationChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.refreshReportPeriodTitles();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.Iid && !changes.Iid.isFirstChange()) {
      this.clearReportData();
      this.loadReport();
    }

    if (changes.iSelectedConversionActions && !changes.iSelectedConversionActions.isFirstChange() && this.hasRunReport) {
      this.applySelectedConversionActions();
      this.computeSelectedConversionTotals();
    }
  }

  openCampaignAnalysisModal(): void {
    const campaignId = this.Iid || this.route.snapshot.paramMap.get('id');
    if (!campaignId || !this.isInternalUser) {
      return;
    }

    const modalRef = this.modalService.open(CampaignAnalysisModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.campaignId = campaignId;
    modalRef.componentInstance.isAdmin = this.isAdmin;
    modalRef.componentInstance.dateStart = this.DateStart;
    modalRef.componentInstance.dateStop = this.DateStop;
  }

  clearReportData() {
    this.reportMetrics = {};
    this.previousReportMetrics = {};
    this.dailyLeads = [];
    this.hasRunReport = false;
    this.platform = '';
    this.campaignName = '';
    this.setReportLoading(true);
    this.validPlatform = true;
  }

  private setReportLoading(isLoading: boolean): void {
    this.loadCompleted = !isLoading;
    this.appService.setReportViewLoading(isLoading);
  }

  async loadReport() {
    this.setReportLoading(true);
    if (!this.appService.getPageTitle$().getValue()) {
      this.appService.setPageTitle$('REPORTS');
    }
    await this.getSupportedReportingPeriods();

    this.DateStart = this.reportPeriods.find(f => f.key == 'Past Week')?.startDate || '';
    this.DateStop = this.reportPeriods.find(f => f.key == 'Past Week')?.stopDate || '';
    this.selectedPeriod = 'Past Week';
    this.emitReportDateRange();

    this.reportForm.patchValue({
      key: this.selectedPeriod,
      startDate: this.DateStart,
      stopDate: this.DateStop,
    });

    const campaignId = this.Iid || this.route.snapshot.paramMap.get('id');
    try {
      if (campaignId) {
        await this.loadCampaignDetails(campaignId);
      }
    } catch (error) {
      console.error('Error loading campaign details:', error);
      this.validPlatform = false;
    }
    this.setReportLoading(false);
  }

  getSupportedReportingPeriods(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reportsApiService.getSupportedReportingPeriods()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: Array<IReportingPeriod>) => {
          this.reportPeriods = (response ?? []).map(period => {
            const displayKey = this.toReportingPeriodDisplayKey(period.key);
            return {
              ...period,
              displayKey,
              title: displayKey ? this.translate.instant(displayKey) : (period.key ?? '')
            };
          });
          resolve();
        }, reject);
    });
  }

  private refreshReportPeriodTitles(): void {
    this.reportPeriods = (this.reportPeriods ?? []).map(p => ({
      ...p,
      title: p.displayKey ? this.translate.instant(p.displayKey) : (p.key ?? '')
    }));
  }

  private toReportingPeriodCode(key?: string | null): string {
    const raw = (key ?? '').trim();
    if (!raw) {
      return '';
    }

    const normalized = raw
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const aliasMap: Record<string, string> = {
      THIS_WEEK: 'CURRENT_WEEK',
      THIS_MONTH: 'CURRENT_MONTH',
      THIS_YEAR: 'CURRENT_YEAR',
      LAST_WEEK: 'PAST_WEEK',
      LAST_30_DAYS: 'PAST_30_DAYS',
      PREVIOUS_30_DAYS: 'PAST_30_DAYS',
      LAST_MONTH: 'PREVIOUS_MONTH',
      LAST_YEAR: 'PREVIOUS_YEAR'
    };

    return aliasMap[normalized] ?? normalized;
  }

  private toReportingPeriodDisplayKey(key?: string | null): string {
    const code = this.toReportingPeriodCode(key);
    return code ? `REPORTING_PERIOD.${code}` : '';
  }

  getLeads() {
    // this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Leads);
    let reachCampaignID = this.Iid || this.route.snapshot.paramMap.get('id');
    // console.log('Fetching leads for Campaign ID:', reachCampaignID, 'and Campaign Name:', this.campaignName);
    this.leadsApi.getAllLeads({search: `${this.campaignName}` })
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.leads) {
          const dateStop = this.DateStop?.length === 10
            ? new Date(this.DateStop + 'T23:59:59.999Z')
            : new Date(this.DateStop!);
          this.leadsData = [...response.leads].filter((x: ILead) => 
            x.campaignId == reachCampaignID &&
            new Date(x.createdAt) >= new Date(this.DateStart!) &&
            new Date(x.createdAt) <= dateStop
          );
          
          this.leadsTotalCount = this.leadsData.length;
          this.membersData = this.leadsData.filter((x: ILead) => x.isMember === true);
          this.membersTotalCount = this.membersData.length;
        }
    });
  }

  async getReportData(): Promise<void> {
    this.setReportLoading(true);
    let reachCampaignID = this.iReportData
      ? null
      : (this.Iid || this.route.snapshot.paramMap.get('id'));
    if (reachCampaignID) {
      await this.loadCampaignDetails(reachCampaignID);
      this.getLeads();
    }

    if (this.platform === 'Google' || this.platform === 'Facebook' || this.platform === 'Meta') {
      let PerformanceMetrics = '7';

      if ((reachCampaignID || this.iReportData) && this.platform && this.DateStart && this.DateStop) {
        try {
          const previousDateRange = this.iReportData
            ? null
            : this.getPreviousDateRange(this.DateStart, this.DateStop);
          const data = this.iReportData || await this.fetchCampaignReportData(
            reachCampaignID as string,
            this.platform,
            this.DateStart,
            this.DateStop,
            PerformanceMetrics,
          );
          console.log('Fetched report data:', data);

          if (!data.ErrorMessage && data.Platform) {
            this.currentReportData = data;
            this.initializeConversionActions(data);
            this.reportMetrics.noData = '';
            this.reportMetrics.Platform = this.normalizePlatform(data.Platform.trim());
            this.reportMetrics.DateRange = (data.GraphDateRange || '').toString().trim();
            this.reportMetrics.Currency = data.CurrencyCode || (this.instance.is('cpjp') ? 'JPY' : 'USD');

            this.previousReportMetrics = {};
            this.previousReportData = null;
            let previousData: any = null;

            try {
              if (!this.iReportData) {
                previousData = await this.fetchCampaignReportData(
                  reachCampaignID as string,
                  this.platform,
                  previousDateRange!.startDate,
                  previousDateRange!.stopDate,
                  PerformanceMetrics,
                );

                if (!previousData?.ErrorMessage && previousData?.Platform) {
                  this.previousReportData = previousData;
                  this.previousReportMetrics = this.buildOverviewMetrics(previousData);
                }
              }
            } catch (previousError) {
              console.error('Error fetching previous raw campaigns data:', previousError);
            }

            // Update columns for currency and set currencyPipeFormat
            this.updateCurrencyColumns();


            this.getOverviewMetrics(data);
            this.updateReportMetrics();
            this.getIndividualCampaignsMetrics(data);

            // Rebuild default columns now that conversion-action availability is known.
            this.updateCurrencyColumns();

            if (this.reportMetrics.Platform === 'Google') {
              this.allCampaignsColumns = [
                { title: 'CAMPAIGN', field: 'Name', sortable: true, searchable: true },
                ...this.defaultGoogleColumns
              ];
              this.devicesColumns = [
                { title: 'DEVICE', field: 'Device', sortable: true, searchable: true },
                ...this.defaultGoogleColumns
              ];
              this.googleKeywordsColumns = [
                { title: 'KEYWORD', field: 'Keyword', sortable: true, searchable: true },
                ...this.defaultGoogleColumns
              ];

              this.getGoogleKeywordsMetrics(data);
              this.getGoogleDevicesMetrics(data);

              this.reportForm.patchValue({
                showKeywords: true,
                showAds: false,
                showDemographics: false
              });
            } else if (this.reportMetrics.Platform === 'Meta') {
              this.allCampaignsColumns = [
                { title: 'CAMPAIGN', field: 'Name', sortable: true, searchable: true },
                ...this.defaultMetaColumns
              ];
              this.devicesColumns = [
                { title: 'DEVICE', field: 'Device', sortable: true, searchable: true },
                ...this.defaultMetaColumns
              ];
              this.adsColumns = [
                // { title: 'THUMBNAIL', type: 'image', width: '75px', field: 'ThumbnailUrl', sortable: false, searchable: false },
                { title: 'AD', type: 'html', width: '370px', field: 'AdText', exportField: 'AdTextExport', sortable: false, searchable: false },
                { title: 'FREQUENCY', field: 'Frequency', sortable: true, pipe: 'NumberPipe', pipeArgs: [[2]] },
                ...this.defaultMetaColumns
              ];
              this.metaDemographicsColumns = [
                { title: 'AGE', field: 'Age', sortable: true, searchable: true },
                { title: 'GENDER', field: 'Gender', sortable: true, searchable: true },
                ...this.defaultMetaColumns
              ];
              this.metaAgeDemographicsColumns = [
                { title: 'AGE', field: 'Age', sortable: true, searchable: true },
                ...this.defaultMetaColumns
              ];
              this.metaGenderDemographicsColumns = [
                { title: 'GENDER', field: 'Gender', sortable: true, searchable: true },
                ...this.defaultMetaColumns
              ];

              this.getMetaAdsMetrics(data);
              this.getMetaDemographicsMetrics(data);
              this.getMetaDevicesMetrics(data);

              this.reportForm.patchValue({
                showKeywords: false,
                showAds: true,
                showDemographics: true
              });
            }

            this.computeSelectedConversionTotals();
            this.dailyLeads = this.getDailyLeads(data);
            this.updateDailyLeadsChart();
            this.hasRunReport = true;
            this.setReportLoading(false);
          } else {
            this.reportMetrics = {};
            this.previousReportMetrics = {};
            this.reportMetrics.noData = 'NO_REPORT_DATA';
            this.hasRunReport = true;
            this.setReportLoading(false);
          }
        } catch (error) {
          console.error('Error fetching raw campaigns data:', error);
          this.previousReportMetrics = {};
          this.hasRunReport = true;
          this.setReportLoading(false);
        }
      }
      // console.log('Report Data:', this.reportMetrics);
    } else {
      console.error('Invalid Platform:', this.platform);
      this.hasRunReport = true;
      this.setReportLoading(false);
      this.validPlatform = false;
    }
  } 

  toTitleCase(str: string): string {
    return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  private normalizePlatform(platform: string): string {
    return platform?.trim().toLowerCase() === 'facebook' ? 'Meta' : platform;
  }

  private normalizeReportDate(value: any): string | null {
    if (!value) {
      return null;
    }

    const dateValue = value instanceof Date ? value.toISOString() : value.toString();
    return dateValue.length >= 10 ? dateValue.slice(0, 10) : null;
  }

  loadCampaignDetails(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.campaignsApiService.getCampaignDetails(id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.platform = response.data.platform;
          this.campaignName = response.data.name;
          resolve();
        }, reject);
    });
  }

  ngOnDestroy(): void {
    this.appService.setReportViewLoading(false);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getTotalMetrics(data: any, item: string): number {
    let total = 0;
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const metric = data[key].Metrics;
        if (metric && metric[item]) {
          total += metric[item];
        }
      }
    }
    return total;
  }
  getDailyLeads(data: any): any {
    if (this.hasConversionActionBreakdown && Array.isArray(data?.DaySplitsAdsPdfModels)) {
      const dailyTotals = new Map<string, number>();
      const getDailyModels = (models: any[]): any[] => models.flatMap(model => {
        const nestedModels = model?.DaySplitsAdsPdfModels;
        return Array.isArray(nestedModels) && nestedModels.length > 0
          ? getDailyModels(nestedModels)
          : [model];
      });

      for (const model of getDailyModels(data.DaySplitsAdsPdfModels)) {
        const dates = Array.isArray(model?.Dates) ? model.Dates : [];
        if (dates.length !== 1) {
          continue;
        }

        const conversions = this.getSelectedConversionCount(model);
        const value = dates[0];
        const date = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
        dailyTotals.set(date, (dailyTotals.get(date) ?? 0) + conversions);
      }

      if (dailyTotals.size > 0) {
        return Array.from(dailyTotals, ([Date, Conversions]) => ({ Date, Conversions }))
          .sort((a, b) => a.Date.localeCompare(b.Date));
      }
    }

    const leads: Array<{ Date: string; Conversions: number }> = [];
    for (let i = 0; i < (data?.Data?.length ?? 0); i++) {
      leads.push({
        Conversions: this.toNumber(data.Data[i]),
        Date: data.Dates?.[i] ?? ''
      });
    }

    leads.sort((a: any, b: any) => (a.Date && b.Date) ? a.Date.localeCompare(b.Date) : 0);
    return leads;
  }

  private updateDailyLeadsChart(): void {
    const leads = this.recentLeads;
    const values = leads
      .map(lead => Number(lead.Conversions))
      .filter(value => Number.isFinite(value));
    const minimum = values.length > 0 ? Math.min(...values) : 0;
    const maximum = values.length > 0 ? Math.max(...values) : 0;
    const range = maximum - minimum;
    const padding = range > 0
      ? range * 0.1
      : Math.max(maximum * 0.1, 1);
    const paddedMinimum = minimum > 0 ? Math.max(0, minimum - padding) : 0;
    const paddedMaximum = maximum + padding;
    const rawStep = (paddedMaximum - paddedMinimum) / 6;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
    const normalizedStep = rawStep / magnitude;
    const niceStep = normalizedStep <= 1 ? 1 : normalizedStep <= 2 ? 2 : normalizedStep <= 5 ? 5 : 10;
    const step = Math.max(1, niceStep * magnitude);
    const axisMinimum = Math.floor(paddedMinimum / step) * step;
    const axisMaximum = Math.ceil(paddedMaximum / step) * step;
    const chartScales: any = this.dailyLeadsChartOptions.scales;
    chartScales.yAxes[0].ticks.min = axisMinimum;
    chartScales.yAxes[0].ticks.max = axisMaximum;
    chartScales.yAxes[0].ticks.stepSize = step;

    this.dailyLeadsChartLabels = leads.map(lead => this.formatChartDate(lead.Date));
    this.dailyLeadsChartDatasets = [{
      data: leads.map(lead => lead.Conversions),
      label: this.conversionActionBreakdown.length === 1
        ? this.translate.instant(this.conversionActionBreakdown[0].labelKey ?? 'CONVERSIONS')
        : this.hasMultipleConversionActions
          ? this.translate.instant('CONVERSION_ACTIONS')
          : this.translate.instant('LEADS')
    }];

    const color = this.platform === 'Google' ? '#EA4335' : '#1877F2';
    this.dailyLeadsChartColors = [{
      borderColor: color,
      backgroundColor: this.platform === 'Google' ? 'rgba(234, 67, 53, 0.12)' : 'rgba(24, 119, 242, 0.12)',
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: color
    }];
  }

  private formatChartDate(value: string): string {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  convertMicrosToDollars(micros: number): number {
    return micros / 1000000; // 1 micros = 1/1000000 dollar
  }

  getMaxConversions() {
    return Math.max(...this.dailyLeads.map(lead => lead.Conversions));
  }
  
  get recentLeads() {
    return this.dailyLeads.slice(-62);
  }

  onReportPeriodChange(selected: any) {
    const selectedKey = typeof selected === 'string' ? selected : (selected?.key ?? '');
    if (!selectedKey) {
      return;
    }

    let period = this.reportPeriods.find(f => f.key == selectedKey);
    if (selectedKey === 'Custom') {
      // For Custom, keep current startDate/stopDate values, just set the key
      this.reportForm.patchValue({
        key: selectedKey
      });
      this.emitReportDateRange();
    } else {
      this.DateStart = period?.startDate as string;
      this.DateStop = period?.stopDate as string;
      this.reportForm.patchValue({
        key: selectedKey,
        startDate: this.DateStart,
        stopDate: this.DateStop,
      });
      this.emitReportDateRange();
      // Optionally, you could auto-refresh for non-Custom periods here if desired
      // await this.getReportData();
    }
  }
  get selectedReportPeriod(): string | undefined | null { return this.reportForm?.controls?.key?.value; }
  get datesReadonly(): boolean { return false; }

  private setCustomPeriodForManualDates(startDate: string | null, stopDate: string | null): void {
    const selectedKey = this.reportForm.get('key')?.value;
    if (!selectedKey || selectedKey === 'Custom') {
      return;
    }

    const selectedPeriod = this.reportPeriods.find(period => period.key === selectedKey);
    if (selectedPeriod?.startDate !== startDate || selectedPeriod?.stopDate !== stopDate) {
      this.reportForm.patchValue({ key: 'Custom' });
    }
  }

  updateReport() {
    if (this.reportForm.invalid) {
      return;
    }
    this.setReportLoading(true);
    this.DateStart = this.reportForm.get('startDate')?.value;
    this.DateStop = this.reportForm.get('stopDate')?.value;
    this.setCustomPeriodForManualDates(this.DateStart, this.DateStop);
    this.selectedPeriod = this.reportForm.get('key')?.value;
    this.emitReportDateRange();
    this.getReportData();
  }

  formatAdBody(body: string): string {
    let shortened = body.length > 128 ? body.substring(0, 128) + '...' : body;
    let html = shortened.replace(/(?:\r\n|\r|\n)/g, '<br>');
    return html;
  }

  async convertImageUrlToBase64(imageUrl: string): Promise<string> {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  async getImageUrl(imageUrl: string): Promise<string> {
    const base64ImageData = await this.convertImageUrlToBase64(imageUrl);
    // Remove the data URL prefix to get only the base64 string
    const base64String = base64ImageData.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
    return base64String;
  }

  private async fetchCampaignReportData(
    reachCampaignID: string,
    platform: string,
    startDate: string,
    stopDate: string,
    performanceMetrics: string,
  ): Promise<any> {
    return this.reportsApiService
      .getRawCampaignsData(reachCampaignID, platform, startDate, stopDate, performanceMetrics)
      .pipe(takeUntil(this.destroyed$))
      .toPromise();
  }

  private getPreviousDateRange(startDate: string, stopDate: string): { startDate: string; stopDate: string } {
    const currentStart = this.parseIsoDate(startDate);
    const currentStop = this.parseIsoDate(stopDate);
    const rangeInDays = Math.max(1, Math.round((currentStop.getTime() - currentStart.getTime()) / 86400000) + 1);

    const previousStop = new Date(currentStart);
    previousStop.setUTCDate(previousStop.getUTCDate() - 1);

    const previousStart = new Date(previousStop);
    previousStart.setUTCDate(previousStart.getUTCDate() - (rangeInDays - 1));

    return {
      startDate: this.formatIsoDate(previousStart),
      stopDate: this.formatIsoDate(previousStop),
    };
  }

  private parseIsoDate(value: string): Date {
    return new Date(`${value}T00:00:00.000Z`);
  }

  private formatIsoDate(value: Date): string {
    return value.toISOString().slice(0, 10);
  }

  private buildOverviewMetrics(data: any) {
    const Clicks = data.Clicks > 0 ? data.Clicks : 0;
    const Impressions = data.Impressions > 0 ? data.Impressions : 0;
    const Leads = this.getSelectedConversionsFromData(data);
    const Cost = data.Cost > 0 ? data.Cost : 0;

    return {
      Clicks,
      Impressions,
      Leads,
      SelectedConversionActionsTotal: Leads,
      Cost,
      Ctr: Impressions > 0 ? Clicks / Impressions : 0,
      Cpc: Clicks > 0 ? Cost / Clicks : 0,
      Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
      Lsr: Clicks > 0 ? Leads / Clicks : 0,
      Cpl: Leads > 0 ? Cost / Leads : 0,
    };
  }

  getDeltaInfo(
    current: number,
    previous: number,
    invertColor: boolean = false,
  ): { direction: 'up' | 'down' | 'flat'; pct: number | null; className: string } {
    const currentValue = Number.isFinite(current) ? current : 0;
    const previousValue = Number.isFinite(previous) ? previous : 0;

    let direction: 'up' | 'down' | 'flat' = 'flat';
    if (currentValue > previousValue) {
      direction = 'up';
    } else if (currentValue < previousValue) {
      direction = 'down';
    }

    let pct: number | null = null;
    if (previousValue === 0) {
      pct = currentValue === 0 ? 0 : null;
    } else {
      pct = (currentValue - previousValue) / Math.abs(previousValue);
    }

    if (pct !== null && Math.round(Math.abs(pct) * 100) === 0) {
      return { direction: 'flat', pct: 0, className: 'text-muted' };
    }

    if (direction === 'flat') {
      return { direction, pct, className: 'text-muted' };
    }

    const isGood = invertColor ? direction === 'down' : direction === 'up';
    return { direction, pct, className: isGood ? 'text-success' : 'text-danger' };
  }

  getOverviewMetrics(data: any) {
    Object.assign(this.reportMetrics, this.buildOverviewMetrics(data));

    this.reportMetrics.ReachLeads = this.leadsTotalCount;
    this.reportMetrics.Members = this.membersTotalCount;
    this.reportMetrics.CpMember = this.membersTotalCount > 0 ? this.reportMetrics.Cost / this.membersTotalCount : 0;

  }

  getIndividualCampaignsMetrics(data: any) {
    // Map campaigns, include both `Leads` (legacy) and `Conversions` fields
    const campaigns = data.DaySplitsAdsPdfModels.map((model: any) => {
      const Clicks = model.Clicks > 0 ? model.Clicks : 0;
      const Impressions = model.Impressions > 0 ? model.Impressions : 0;
      const Conversions = model.Conversions > 0 ? model.Conversions : 0;
      const Cost = model.Cost > 0 ? model.Cost : 0;

      const campaignObj: any = {
        Name: (model.CampaignName || '').toString().trim(),
        Clicks,
        Impressions,
        Leads: Conversions, // keep legacy field
        Conversions,
        Cost,
        Ctr: (Impressions > 0 ? Clicks / Impressions : 0),
        Cpc: Clicks > 0 ? Cost / Clicks : 0,
        Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
        Lsr: (Clicks > 0 ? Conversions / Clicks : 0),
        Cpl: Conversions > 0 ? Cost / Conversions : 0
      };
      this.setRowConversionActionCounts(campaignObj, model);

      if (model.NoDataForReportingPeriod) {
        campaignObj.NoDataForReportingPeriod = true;
      }

      return campaignObj;
    });

    if (this.iIsRollupReport) {
      const grouped = new Map<string, any>();
      for (const campaign of campaigns) {
        const existing = grouped.get(campaign.Name);
        if (!existing) {
          grouped.set(campaign.Name, { ...campaign });
          continue;
        }

        for (const field of ['Clicks', 'Impressions', 'Leads', 'Conversions', 'Cost']) {
          existing[field] += campaign[field];
        }

        this.addConversionActionCounts(existing, campaign);

        existing.NoDataForReportingPeriod = existing.NoDataForReportingPeriod && campaign.NoDataForReportingPeriod;
        existing.Ctr = existing.Impressions > 0 ? existing.Clicks / existing.Impressions : 0;
        existing.Cpc = existing.Clicks > 0 ? existing.Cost / existing.Clicks : 0;
        existing.Cpm = existing.Impressions > 0 ? (existing.Cost / existing.Impressions) * 1000 : 0;
        existing.Lsr = existing.Clicks > 0 ? existing.Conversions / existing.Clicks : 0;
        existing.Cpl = existing.Conversions > 0 ? existing.Cost / existing.Conversions : 0;
      }

      this.reportMetrics.AllCampaigns = Array.from(grouped.values());
    } else {
      this.reportMetrics.AllCampaigns = campaigns;
    }

    this.applySelectedConversionsToRows(this.reportMetrics.AllCampaigns);
  }

  // Recalculate conversion totals and dependent rate/cost metrics for the selected actions.
  computeSelectedConversionTotals(): void {
    const tableRows = [
      this.reportMetrics.AllCampaigns,
      this.reportMetrics.Ads,
      this.reportMetrics.Keywords,
      this.reportMetrics.Devices,
      this.reportMetrics.Demographics,
      this.reportMetrics.AgeDemographics,
      this.reportMetrics.GenderDemographics,
      this.reportMetrics.AgeGenderDemographics
    ];
    tableRows.forEach(rows => this.applySelectedConversionsToRows(rows));

    const conversions = this.getSelectedConversionsFromData(this.currentReportData);
    const clicks = this.toNumber(this.reportMetrics.Clicks);
    const cost = this.toNumber(this.reportMetrics.Cost);
    this.reportMetrics.SelectedConversionActionsTotal = conversions;
    this.reportMetrics.Conversions = conversions;
    this.reportMetrics.Leads = conversions;
    this.reportMetrics.Lsr = clicks > 0 ? conversions / clicks : 0;
    this.reportMetrics.Cpl = conversions > 0 ? cost / conversions : 0;

    if (this.previousReportData) {
      const previousConversions = this.getSelectedConversionsFromData(this.previousReportData);
      const previousClicks = this.toNumber(this.previousReportData.Clicks);
      const previousCost = this.toNumber(this.previousReportData.Cost);
      this.previousReportMetrics.SelectedConversionActionsTotal = previousConversions;
      this.previousReportMetrics.Leads = previousConversions;
      this.previousReportMetrics.Lsr = previousClicks > 0 ? previousConversions / previousClicks : 0;
      this.previousReportMetrics.Cpl = previousConversions > 0 ? previousCost / previousConversions : 0;
    }

    this.reportMetrics.AllCampaigns = [...(this.reportMetrics.AllCampaigns ?? [])];
    this.reportMetrics.Ads = [...(this.reportMetrics.Ads ?? [])];
    this.reportMetrics.Keywords = [...(this.reportMetrics.Keywords ?? [])];
    this.reportMetrics.Devices = [...(this.reportMetrics.Devices ?? [])];
    this.reportMetrics.Demographics = [...(this.reportMetrics.Demographics ?? [])];
    this.reportMetrics.AgeDemographics = [...(this.reportMetrics.AgeDemographics ?? [])];
    this.reportMetrics.GenderDemographics = [...(this.reportMetrics.GenderDemographics ?? [])];
    this.reportMetrics.AgeGenderDemographics = [...(this.reportMetrics.AgeGenderDemographics ?? [])];

    this.refreshConversionColumnTitles();

    if (this.currentReportData) {
      this.dailyLeads = this.getDailyLeads(this.currentReportData);
      this.updateDailyLeadsChart();
    }
  }

  private refreshConversionColumnTitles(): void {
    this.updateCurrencyColumns();

    const campaignColumns = this.allCampaignsColumns.filter(column => !column.field?.startsWith('action_'));
    const actionColumns = this.conversionActionBreakdown
      .filter(action => (action.value ?? 0) > 0 && !(this.isLeadOnlyReport && action.key === 'leads'))
      .map(action => ({
        title: action.labelKey ?? (action.label ?? action.key).toString().toUpperCase(),
        field: `action_${(action.key ?? action.label).toString().replace(/[^a-z0-9]/gi, '_')}`,
        sortable: true
      }));
    this.allCampaignsColumns = [...campaignColumns, ...actionColumns];

    const titles = new Map<string, string>();
    for (const column of [...this.defaultGoogleColumns, ...this.defaultMetaColumns]) {
      titles.set(column.field, column.title);
    }

    const tables = [
      this.allCampaignsColumns,
      this.devicesColumns,
      this.adsColumns,
      this.googleKeywordsColumns,
      this.metaDemographicsColumns,
      this.metaAgeDemographicsColumns,
      this.metaGenderDemographicsColumns
    ];
    for (const table of tables) {
      for (const column of table) {
        if (titles.has(column.field)) {
          column.title = titles.get(column.field);
        }
      }
    }
  }

  getGoogleAdsMetrics(data: any) {
    this.reportMetrics.Ads = data.GoogleTopFives.map((ad: any) => {
      const Clicks = ad.clicks > 0 ? ad.clicks : 0;
      const Impressions = ad.impressions > 0 ? ad.impressions : 0;
      const Leads = ad.conversions > 0 ? ad.conversions : 0;
      const Conversions = Leads;
      const Cost = ad.cost > 0 ? ad.cost : 0;
      const row = {
        Clicks,
        Impressions,
        Leads,
        Conversions,
        Cost,
        Ctr: Impressions > 0 ? Clicks / Impressions : 0,
        Cpc: Clicks > 0 ? Cost / Clicks : 0,
        Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
        Lsr: Clicks > 0 ? Leads / Clicks : 0,
        Cpl: Leads > 0 ? Cost / Leads : 0,
        Desc1: ad.desc1,
        Desc2: ad.desc2,
        HeadlinePart1: ad.headlinePart1,
        HeadlinePart2: ad.headlinePart2,
        HeadlinePart3: ad.headlinePart3
      };
      this.setRowConversionActionCounts(row, ad);
      return row;
    });
  }

  getGoogleKeywordsMetrics(data: any) {
    this.reportMetrics.Keywords = data.GoogleKeywords.map((keyword: any) => {
      const Clicks = keyword.clicks > 0 ? keyword.clicks : 0;
      const Impressions = keyword.impressions > 0 ? keyword.impressions : 0;
      const Leads = keyword.conversions > 0 ? keyword.conversions : 0;
      const Conversions = Leads;
      const Cost = keyword.cost > 0 ? keyword.cost : 0;
      const row = {
        Clicks,
        Impressions,
        Leads,
        Conversions,
        Cost,
        Ctr: Impressions > 0 ? Clicks / Impressions : 0,
        Cpc: Clicks > 0 ? Cost / Clicks : 0,
        Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
        Lsr: Clicks > 0 ? Leads / Clicks : 0,
        Cpl: Leads > 0 ? Cost / Leads : 0,
        Keyword: keyword.keyword
      };
      this.setRowConversionActionCounts(row, keyword);
      return row;
    }).reduce((acc: any, curr: any) => {
      const existingKeyword = acc.find((item: any) => item.Keyword === curr.Keyword);
      if (existingKeyword) {
        existingKeyword.Clicks += curr.Clicks;
        existingKeyword.Impressions += curr.Impressions;
        existingKeyword.Leads += curr.Leads;
        existingKeyword.Cost = existingKeyword.Cost + curr.Cost;
        this.addConversionActionCounts(existingKeyword, curr);
        existingKeyword.Ctr = existingKeyword.Impressions > 0 ? existingKeyword.Clicks / existingKeyword.Impressions : 0;
        existingKeyword.Cpc = existingKeyword.Clicks > 0 ? existingKeyword.Cost / existingKeyword.Clicks : 0;
        existingKeyword.Cpm = existingKeyword.Impressions > 0 ? (existingKeyword.Cost / existingKeyword.Impressions) * 1000 : 0;
        existingKeyword.Lsr = existingKeyword.Clicks > 0 ? existingKeyword.Leads / existingKeyword.Clicks : 0;
        existingKeyword.Cpl = existingKeyword.Leads > 0 ? existingKeyword.Cost / existingKeyword.Leads : 0;
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);
  }

  getGoogleDevicesMetrics(data: any) {
    this.reportMetrics.Devices = data.GoogleDevices.map((device: any) => {
      const Clicks = device.clicks > 0 ? device.clicks : 0;
      const Impressions = device.impressions > 0 ? device.impressions : 0;
      const Leads = device.conversions > 0 ? device.conversions : 0;
      const Conversions = Leads;
      const Cost = device.cost > 0 ? device.cost : 0;
      const row = {
        Clicks,
        Impressions,
        Leads,
        Conversions,
        Cost,
        Ctr: Impressions > 0 ? Clicks / Impressions : 0,
        Cpc: Clicks > 0 ? Cost / Clicks : 0,
        Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
        Lsr: Clicks > 0 ? Leads / Clicks : 0,
        Cpl: Leads > 0 ? Cost / Leads : 0,
        Device: device.device
      };
      this.setRowConversionActionCounts(row, device);
      return row;
    }).reduce((acc: any, curr: any) => {
      const existingDevice = acc.find((item: any) => item.Device === curr.Device);
      if (existingDevice) {
        existingDevice.Clicks += curr.Clicks;
        existingDevice.Impressions += curr.Impressions;
        existingDevice.Leads += curr.Leads;
        existingDevice.Cost = existingDevice.Cost + curr.Cost;
        this.addConversionActionCounts(existingDevice, curr);
        existingDevice.Ctr = existingDevice.Impressions > 0 ? existingDevice.Clicks / existingDevice.Impressions : 0;
        existingDevice.Cpc = existingDevice.Clicks > 0 ? existingDevice.Cost / existingDevice.Clicks : 0;
        existingDevice.Cpm = existingDevice.Impressions > 0 ? (existingDevice.Cost / existingDevice.Impressions) * 1000 : 0;
        existingDevice.Lsr = existingDevice.Clicks > 0 ? existingDevice.Leads / existingDevice.Clicks : 0;
        existingDevice.Cpl = existingDevice.Leads > 0 ? existingDevice.Cost / existingDevice.Leads : 0;
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);
  }

  getMetaAdsMetrics(data: any): void {
    const ads = data.FacebookTopFives.map((ad: any) => {
      const Clicks = ad.clicks > 0 ? ad.clicks : 0;
      const Frequency = Number(ad.frequency) > 0 ? Number(ad.frequency) : 0;
      const Impressions = ad.impressions > 0 ? ad.impressions : 0;
      const Leads = Number(ad.leads) > 0 ? Number(ad.leads) : 0;
      const Conversions = Leads;
      const Cost = Number(ad.spend) ? Number(ad.spend) : 0;
      const AdId = ad.ad_id;
      // Re-enable this when thumbnail rendering is restored.
      // const ThumbnailUrl = await this.getImageUrl(ad.FbTopFiveModel[0].thumbnail_url);
      const AdText = ad.FbTopFiveModel[0].title;
      const row = {
        Clicks,
        Frequency,
        Impressions,
        Leads,
        Conversions,
        Cost,
        Ctr: Impressions > 0 ? Clicks / Impressions : 0,
        Cpc: Clicks > 0 ? Cost / Clicks : 0,
        Cpm: Impressions > 0 ? (Cost / Impressions) * 1000 : 0,
        Lsr: Clicks > 0 ? Leads / Clicks : 0,
        Cpl: Leads > 0 ? Cost / Leads : 0,
        Body: ad.FbTopFiveModel[0].body,
        // ThumbnailUrl,
        Title: ad.FbTopFiveModel[0].title,
        AdName: ad.FbTopFiveModel[0].ad_name,
        AdId,
        AdText,
        AdTextExport: AdText
      };
      this.setRowConversionActionCounts(row, ad);
      return row;
    });
    this.reportMetrics.Ads = ads;
  }

  loadVisibleMetaAdPreviews(ads: Array<any>): void {
    ads.forEach(ad => {
      this.fetchAdPreview(ad.AdId)
        .then(adPreview => {
          const currentAd = this.reportMetrics.Ads.find((reportAd: any) => reportAd.AdId === ad.AdId);
          if (currentAd) {
            currentAd.AdText = adPreview;
          }
        })
        .catch(() => {
          // Keep the title fallback when an ad preview is unavailable.
        });
    });
  }

  private async fetchAdPreview(adId: string): Promise<string> {
    const existingRequest = this.adPreviewRequests.get(adId);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      ad_id: adId,
      ad_format: 'MOBILE_FEED_STANDARD'
    });

    const request = fetch(
      `${environment.apiBaseUrl}/dashboard/ad-preview?${params.toString()}`
    ).then(async response => {
      if (!response.ok) {
        throw new Error(`Ad preview request failed: ${response.status}`);
      }

      const result: { data?: Array<{ body?: string }> } = await response.json();
      return result.data?.[0]?.body ?? '';
    });

    this.adPreviewRequests.set(adId, request);
    request.catch(() => this.adPreviewRequests.delete(adId));
    return request;
  }

  getMetaDemographicsMetrics(data: any) {
    this.reportMetrics.Demographics = data.FacebookDemographics.map((demographic: any) => {
      const mergedActions: any = {};
    
      demographic.actionsList.forEach((actionsList: any) => {
        const key = `${actionsList.age}-${actionsList.gender}`;
        if (!mergedActions[key]) {
          mergedActions[key] = {
            Clicks: 0,
            Impressions: 0,
            Leads: 0,
            Cost: 0,
            ActionCounts: {},
            Age: actionsList.age,
            Gender: this.toTitleCase(actionsList.gender)
          };
        }
        this.addConversionActionCounts(
          mergedActions[key],
          this.getMetaActionCounts(actionsList.actions, actionsList)
        );
    
        if (actionsList.actions) {
          actionsList.actions.forEach((action: any) => {
            const value = Number(action.Value);
            if (action.ActionType === 'link_click') {
              mergedActions[key].Clicks += value > 0 ? value : 0;
            } else if (action.ActionType === 'lead') {
              mergedActions[key].Leads += value > 0 ? value : 0;
            }
          });
        } else {
          mergedActions[key].Clicks = 0;
          mergedActions[key].Leads = 0;
        }
    
        mergedActions[key].Impressions += actionsList.impressions > 0 ? actionsList.impressions : 0;
        mergedActions[key].Cost += Number(actionsList.spend) > 0 ? Number(actionsList.spend) : 0;
      });
    
      const mergedArray = Object.values(mergedActions).map((item: any) => {
        const row = {
          Clicks: item.Clicks,
          Impressions: item.Impressions,
          Leads: item.Leads,
          Conversions: item.Leads,
          Cost: item.Cost,
          Ctr: item.Impressions > 0 ? item.Clicks / item.Impressions : 0,
          Cpc: item.Clicks > 0 ? item.Cost / item.Clicks : 0,
          Cpm: item.Impressions > 0 ? (item.Cost / item.Impressions) * 1000 : 0,
          Lsr: item.Clicks > 0 ? item.Leads / item.Clicks : 0,
          Cpl: item.Leads > 0 ? item.Cost / item.Leads : 0,
          Age: item.Age,
          Gender: item.Gender
        };
        this.setRowConversionActionCounts(row, item);
        return row;
      });
    
      return mergedArray;
    }).flat().filter((row: any) => row.Impressions > 0);
    
    this.reportMetrics.AgeDemographics = this.reportMetrics.Demographics.reduce((acc: any, curr: any) => {
      if (!acc[curr.Age]) {
        acc[curr.Age] = {
          Clicks: 0,
          Impressions: 0,
          Leads: 0,
          Cost: 0,
          ActionCounts: {},
          Age: curr.Age
        };
      }
      acc[curr.Age].Clicks += curr.Clicks;
      acc[curr.Age].Impressions += curr.Impressions;
      acc[curr.Age].Leads += curr.Leads;
      acc[curr.Age].Cost += curr.Cost;
      this.addConversionActionCounts(acc[curr.Age], curr);
      return acc;
    }, {});
    
    this.reportMetrics.AgeDemographics = Object.values(this.reportMetrics.AgeDemographics).map((item: any) => {
      const row = {
        Clicks: item.Clicks,
        Impressions: item.Impressions,
        Leads: item.Leads,
        Conversions: item.Leads,
        Cost: item.Cost,
        Ctr: item.Impressions > 0 ? item.Clicks / item.Impressions : 0,
        Cpc: item.Clicks > 0 ? item.Cost / item.Clicks : 0,
        Cpm: item.Impressions > 0 ? (item.Cost / item.Impressions) * 1000 : 0,
        Lsr: item.Clicks > 0 ? item.Leads / item.Clicks : 0,
        Cpl: item.Leads > 0 ? item.Cost / item.Leads : 0,
        Age: item.Age
      };
      this.setRowConversionActionCounts(row, item);
      return row;
    });
    
    this.reportMetrics.GenderDemographics = this.reportMetrics.Demographics.reduce((acc: any, curr: any) => {
      if (!acc[curr.Gender]) {
        acc[curr.Gender] = {
          Clicks: 0,
          Impressions: 0,
          Leads: 0,
          Cost: 0,
          ActionCounts: {},
          Gender: curr.Gender
        };
      }
      acc[curr.Gender].Clicks += curr.Clicks;
      acc[curr.Gender].Impressions += curr.Impressions;
      acc[curr.Gender].Leads += curr.Leads;
      acc[curr.Gender].Cost += curr.Cost;
      this.addConversionActionCounts(acc[curr.Gender], curr);
      return acc;
    }, {});
    
    this.reportMetrics.GenderDemographics = Object.values(this.reportMetrics.GenderDemographics).map((item: any) => {
      const row = {
        Clicks: item.Clicks,
        Impressions: item.Impressions,
        Leads: item.Leads,
        Conversions: item.Leads,
        Cost: item.Cost,
        Ctr: item.Impressions > 0 ? item.Clicks / item.Impressions : 0,
        Cpc: item.Clicks > 0 ? item.Cost / item.Clicks : 0,
        Cpm: item.Impressions > 0 ? (item.Cost / item.Impressions) * 1000 : 0,
        Lsr: item.Clicks > 0 ? item.Leads / item.Clicks : 0,
        Cpl: item.Leads > 0 ? item.Cost / item.Leads : 0,
        Gender: item.Gender
      };
      this.setRowConversionActionCounts(row, item);
      return row;
    });

    this.reportMetrics.AgeGenderDemographics = this.reportMetrics.Demographics.reduce((acc: any, curr: any) => {
      const key = `${curr.Age}-${curr.Gender}`;
      if (!acc[key]) {
        acc[key] = {
          Clicks: 0,
          Impressions: 0,
          Leads: 0,
          Cost: 0,
          ActionCounts: {},
          Age: curr.Age,
          Gender: curr.Gender
        };
      }
      acc[key].Clicks += curr.Clicks;
      acc[key].Impressions += curr.Impressions;
      acc[key].Leads += curr.Leads;
      acc[key].Cost += curr.Cost;
      this.addConversionActionCounts(acc[key], curr);
      return acc;
    }, {});

    this.reportMetrics.AgeGenderDemographics = Object.values(this.reportMetrics.AgeGenderDemographics).map((item: any) => {
      const row = {
        Clicks: item.Clicks,
        Impressions: item.Impressions,
        Leads: item.Leads,
        Conversions: item.Leads,
        Cost: item.Cost,
        Ctr: item.Impressions > 0 ? item.Clicks / item.Impressions : 0,
        Cpc: item.Clicks > 0 ? item.Cost / item.Clicks : 0,
        Cpm: item.Impressions > 0 ? (item.Cost / item.Impressions) * 1000 : 0,
        Lsr: item.Clicks > 0 ? item.Leads / item.Clicks : 0,
        Cpl: item.Leads > 0 ? item.Cost / item.Leads : 0,
        Age: item.Age,
        Gender: item.Gender
      };
      this.setRowConversionActionCounts(row, item);
      return row;
    });
  }

  getMetaDevicesMetrics(data: any) {
    this.reportMetrics.Devices = data.FacebookDevices.map((device: any) => {
      const mergedActions: any = {};
    
      device.actionsList.forEach((actionsList: any) => {
        const key = actionsList.device_platform;
        if (!mergedActions[key]) {
          mergedActions[key] = {
            Clicks: 0,
            Impressions: 0,
            Leads: 0,
            Cost: 0,
            ActionCounts: {},
            Device: this.toTitleCase(actionsList.device_platform)
          };
        }
        this.addConversionActionCounts(
          mergedActions[key],
          this.getMetaActionCounts(actionsList.actions, actionsList)
        );
    
        if (actionsList.actions) {
          actionsList.actions.forEach((action: any) => {
            const value = Number(action.Value);
            if (action.ActionType === 'link_click') {
              mergedActions[key].Clicks += value > 0 ? value : 0;
            } else if (action.ActionType === 'lead') {
              mergedActions[key].Leads += value > 0 ? value : 0;
            }
          });
        } else {
          mergedActions[key].Clicks = 0;
          mergedActions[key].Leads = 0;
        }

        mergedActions[key].Impressions += actionsList.impressions > 0 ? actionsList.impressions : 0;
        mergedActions[key].Cost += Number(actionsList.spend) > 0 ? Number(actionsList.spend) : 0;
      });
    
      return Object.values(mergedActions);
    }).flat().reduce((acc: any, curr: any) => {
      const existingDevice = acc.find((item: any) => item.Device === curr.Device);
      if (existingDevice) {
        existingDevice.Clicks += curr.Clicks;
        existingDevice.Impressions += curr.Impressions;
        existingDevice.Leads += curr.Leads;
        existingDevice.Cost = existingDevice.Cost + curr.Cost;
        this.addConversionActionCounts(existingDevice, curr);
        existingDevice.Ctr = existingDevice.Impressions > 0 ? existingDevice.Clicks / existingDevice.Impressions : 0;
        existingDevice.Cpc = existingDevice.Clicks > 0 ? existingDevice.Cost / existingDevice.Clicks : 0;
        existingDevice.Cpm = existingDevice.Impressions > 0 ? (existingDevice.Cost / existingDevice.Impressions) * 1000 : 0;
        existingDevice.Lsr = existingDevice.Clicks > 0 ? existingDevice.Leads / existingDevice.Clicks : 0;
        existingDevice.Cpl = existingDevice.Leads > 0 ? existingDevice.Cost / existingDevice.Leads : 0;
      } else {
        const row = {
          ...curr,
          Ctr: curr.Impressions > 0 ? curr.Clicks / curr.Impressions : 0,
          Cpc: curr.Clicks > 0 ? curr.Cost / curr.Clicks : 0,
          Cpm: curr.Impressions > 0 ? (curr.Cost / curr.Impressions) * 1000 : 0,
          Lsr: curr.Clicks > 0 ? curr.Leads / curr.Clicks : 0,
          Cpl: curr.Leads > 0 ? curr.Cost / curr.Leads : 0
        };
        this.setRowConversionActionCounts(row, curr);
        acc.push(row);
      }
      return acc;
    }, []);
  }

  updateReportMetrics() {
    const Booked = this.toNumber(this.reportForm.get('Booked')?.value);
    const Show = this.toNumber(this.reportForm.get('Show')?.value);
    const Sold = this.toNumber(this.reportForm.get('Sold')?.value);
    const Leads = this.getSalesFunnelLeadCount();
    const Cost = this.toNumber(this.reportMetrics.Cost);

    this.reportMetrics.FunnelLeads = Leads;
    this.reportMetrics.Booked = Booked;
    this.reportMetrics.Show = Show;
    this.reportMetrics.Sold = Sold;

    if (Leads > 0 && Booked > 0) {
      this.reportMetrics.BookingPercent = Booked / Leads;
    } else {
      this.reportMetrics.BookingPercent = null;
    }
    if (Booked > 0) {
      this.reportMetrics.CostPerBooking = Cost / Booked;
    } else {
      this.reportMetrics.CostPerBooking = null;
    }

    if (Leads > 0 && Show > 0) {
      this.reportMetrics.LeadToShowPercent = Show / Leads;
    } else {
      this.reportMetrics.LeadToShowPercent = null;
    }
    if (Show > 0) {
      this.reportMetrics.CostPerShow = Cost / Show;
    } else {
      this.reportMetrics.CostPerShow = null;
    }

    if (Booked > 0 && Show > 0) {
      this.reportMetrics.BookToShowPercent = Show / Booked;
    } else {
      this.reportMetrics.BookToShowPercent = null;
    }

    if (Leads > 0 && Sold > 0) {
      this.reportMetrics.LeadToSoldPercent = Sold / Leads;
    } else {
      this.reportMetrics.LeadToSoldPercent = null;
    }
    if (Sold > 0) {
      this.reportMetrics.CostPerAcquisition = Cost / Sold;
    } else {
      this.reportMetrics.CostPerAcquisition = null;
    }

    if (Booked > 0 && Sold > 0) {
      this.reportMetrics.BookToSoldPercent = Sold / Booked;
    } else {
      this.reportMetrics.BookToSoldPercent = null;
    }

    if (Show > 0 && Sold > 0) {
      this.reportMetrics.ShowToSoldPercent = Sold / Show;
    } else {
      this.reportMetrics.ShowToSoldPercent = null;
    }
  }

  toggleSelectedConversion(actionKey: string): void {
    if (!actionKey) return;
    const idx = this.selectedConversionActions.indexOf(actionKey);
    if (idx === -1) {
      this.selectedConversionActions.push(actionKey);
    } else {
      this.selectedConversionActions.splice(idx, 1);
    }
    this.computeSelectedConversionTotals();
  }

}
